import { useCallback, useEffect, useState } from 'react'
import { wouldCollideWithSibling } from '@domain/finance'
import type { Category } from '@domain/finance'
import type { NewCategory } from '@domain/ports/finance-repository'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { Botao } from '@ui/glass/Botao'
import { Glass } from '@ui/glass/Glass'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'
import { Problem, SelectField, TextField } from './CrudeFields'
import { DEFECT_LABELS, categoryTreeView, flattenTree } from './category-tree-view'
import type { CategoryNode } from './category-tree-view'
import { CATEGORY_KINDS, optionsOf } from './labels'
import { useCollection } from './use-collection'

/**
 * A árvore de categorias, crua — e a tela em que **duas decisões de produto
 * viram código**, porque nem o domínio nem as regras do Firestore têm como
 * tomá-las.
 *
 * ## 1. Aqui se arquiva; não se apaga
 *
 * `firestore.rules` **permite** apagar qualquer categoria com `isSystem != true`
 * — ou seja, toda categoria criada pelo usuário (decisão 100a). E apagar não
 * devolve o slug: devolve só a **aparência** dele. O slug some da árvore, que é
 * tudo o que `wouldCollideWithSibling` enxerga, e **não** some do histórico.
 * Apague uma "Mercado" sob Alimentação e a próxima nasce herdando
 * `['alimentacao','mercado']` byte a byte, com o histórico da antiga somando na
 * nova: a ambiguidade que **infla** gasto, entrando pela porta do delete
 * (decisão 100b).
 *
 * `category-tree.ts` não fecha essa porta e não teria como — a prova de que o
 * slug foi gasto está nas transações, não na árvore. As regras também não, sem
 * varrer as transações a cada delete. **Quem fecha é esta tela, por escolha**, e
 * a escolha é a mais forte disponível: não existe botão de apagar, e a porta
 * `CategoryRepository` não tem o método. Não é um `confirm()` mais difícil de
 * clicar — é a operação não estar ao alcance.
 *
 * O preço, dito por inteiro: quem quiser mesmo apagar uma categoria vai ao
 * console do Firebase. É deliberado — lá a operação é manual, rara e feita por
 * quem sabe o que está fazendo, que é o oposto de um botão vermelho ao lado de
 * cada linha da lista.
 *
 * ## 2. O estado quebrado aparece
 *
 * Quando a Cloud Function recusa, a transação aborta e o documento do cliente
 * fica gravado sozinho: sem `path` (criação recusada) ou com o `path` velho ao
 * lado do `parentId` novo — inclusive um ciclo de verdade no grafo (decisão
 * 105g). **Nada no tipo carrega o motivo**, então ele é reconstruído da forma
 * da árvore, em `category-tree-view.ts`, e desenhado ao lado de cada linha. As
 * que nenhuma raiz alcança saem numa lista à parte, "fora da árvore" — senão o
 * pior estado do sistema seria também o mais invisível.
 */

const ID = 'categorias'
const ROOT = ''

/**
 * Quanto a tela espera pelo caminho, e de quanto em quanto ela pergunta.
 *
 * Seis segundos porque a função escala a zero: a primeira chamada depois de um
 * tempo parado paga o início frio do runtime, e a decisão 42 já mediu isso em
 * "plausivelmente segundos" para a `bootstrapHousehold`. Menos que isso
 * desistiria antes de o caminho ter chance de chegar.
 */
const ESPERA_MAXIMA = 6000
const INTERVALO = 800

type Draft = {
  name: string
  parentId: string
  kind: Category['kind']
  icon: string
  color: string
}

const EMPTY: Draft = { name: '', parentId: ROOT, kind: 'expense', icon: 'tag', color: '#7c3aed' }

/**
 * O nome de uma categoria **nova** colidiria com uma irmã? **Resposta
 * antecipada, e não a que vale.**
 *
 * Quem de fato recusa é a Cloud Function que cunha o `path`, na mesma transação
 * em que lê a árvore — a concorrência otimista do Firestore vale sobre os
 * documentos lidos e não tranca a *consulta* por irmãs, então uma checagem no
 * cliente não fecha porta nenhuma. O que ela faz é dizer antes de gravar, que é
 * a diferença entre "escolha outro nome" e um `permission-denied` sem
 * explicação. É o que a decisão 104 pede desta tela.
 *
 * **Só na criação.** `wouldCollideWithSibling` recebe **nome** e responde sobre
 * `slugify(nome)`; o slug de uma categoria que já existe é o **estável**, lido
 * do `path` gravado, e renomear não o toca (decisão 98). No rename as duas
 * coisas divergem, e a decisão 104(f) já trazia o contraexemplo com estes
 * nomes: renomear "Mercado" para "Padaria" tendo uma irmã "Padaria" **não muda
 * slug nenhum** e a Function aceita — `renameDocument` escreve só `name`, e a
 * reserva `alimentacao_mercado` já é dela. A tela recusava, e recusava calada.
 * Ver `collisionHint` sendo chamado só com `renamingId === null`, abaixo.
 *
 * O `ignoreId` não some do predicado, mas o único valor que esta tela passa é
 * `null` — porque a única pergunta que ela faz a ele é a da criação.
 *
 * Lança em dois casos, e os dois viram texto: nome que não produz slug, e uma
 * **irmã corrompida** cujo slug estável não é resolvível.
 */
function collisionHint(name: string, parentId: string | null, all: Category[]): string | null {
  if (name.trim() === '') return null
  try {
    return wouldCollideWithSibling(name, parentId, all, null)
      ? 'Já existe uma categoria irmã com esse nome — duas irmãs com o mesmo slug teriam o mesmo caminho.'
      : null
  } catch (error: unknown) {
    return error instanceof Error ? error.message : 'Não foi possível conferir o nome.'
  }
}

/**
 * A cor da categoria — amostra clicável **e** o hex ao lado.
 *
 * Era só um campo de texto onde se digitava `#7c3aed` na mão. Num celular isso
 * é seis caracteres hexadecimais no teclado numérico para escolher uma cor que
 * a pessoa quer **ver**. O seletor nativo resolve o escolher; o texto continua
 * porque é o que permite colar uma cor decidida em outro lugar, e porque é o
 * que o app grava.
 *
 * Os dois escrevem no mesmo estado, então não há como divergirem.
 */
function CorField({ value, onChange }: { value: string; onChange: (cor: string) => void }) {
  const valida = /^#[0-9a-f]{6}$/i.test(value)
  return (
    <p className="mb-2">
      <label htmlFor={`${ID}-cor`} className="block text-xs text-[var(--text-muted)]">
        Cor
      </label>
      <span className="flex items-center gap-2">
        <input
          // `aria-hidden`: quem tem nome é o campo de texto ao lado, e os dois
          // são a mesma cor — anunciar duas vezes seria dizer o mesmo duas vezes.
          aria-hidden="true"
          tabIndex={-1}
          type="color"
          value={valida ? value : '#7c3aed'}
          onChange={(event) => {
            onChange(event.target.value)
          }}
          className="size-9 shrink-0 rounded-[var(--radius-sm)] border border-[var(--border)] bg-transparent"
        />
        <input
          id={`${ID}-cor`}
          type="text"
          value={value}
          onChange={(event) => {
            onChange(event.target.value)
          }}
          className="w-full rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] px-2 py-1 text-sm"
        />
      </span>
    </p>
  )
}

/** A árvore sem os galhos arquivados, para o seletor de pai. */
function vivas(nodes: CategoryNode[]): CategoryNode[] {
  return nodes
    .filter((node) => node.category.archivedAt === null)
    .map((node) => ({ ...node, children: vivas(node.children) }))
}

export function CategoriesScreen() {
  const { categoryRepository } = useFinanceRepositories()
  const load = useCallback(() => categoryRepository.list(), [categoryRepository])
  const { state, reload } = useCollection<Category>(load)

  /**
   * A folha está aberta? `'nova'` cria; um id renomeia; `null` é fechada.
   *
   * O formulário vivia **sempre aberto no fim da página**, abaixo da árvore
   * inteira — para criar era preciso rolar até o fim, e para renomear ele
   * mudava de significado lá embaixo enquanto a linha tocada ficava para trás,
   * fora de vista. Numa folha, ele aparece quando é pedido e some quando não.
   */
  const [folha, setFolha] = useState<'nova' | string | null>(null)
  const renamingId = folha === 'nova' || folha === null ? null : folha
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [failure, setFailure] = useState<string | null>(null)
  /**
   * A categoria recém-criada cujo `path` ainda não chegou.
   *
   * **Quem cunha o caminho é uma Cloud Function**, na transação que também
   * reserva o slug (decisão 105) — o `create` do cliente devolve assim que o
   * documento existe, **antes** disso. A tela relia nesse instante e mostrava
   * a categoria nova como "sem caminho", que é o rótulo do estado quebrado.
   * Parecia que criar não tinha funcionado; funcionou, e o caminho chegava um
   * ou dois segundos depois, sem nada reler.
   *
   * Visto em produção: os logs da função diziam "árvore conferida" para a
   * mesma categoria que a tela chamava de quebrada.
   */
  const [cunhando, setCunhando] = useState<string | null>(null)

  const all = state.status === 'loaded' ? state.items : []

  /**
   * Relê até o caminho da recém-criada aparecer, e desiste depois de um tempo.
   *
   * **Desistir importa tanto quanto esperar.** A função pode *recusar* — slug
   * colidindo com uma irmã, ciclo de parentId —, e nesse caso o caminho não vem
   * nunca. Insistir para sempre esconderia a recusa atrás de um "aguarde"
   * eterno; parando, a linha volta a dizer "sem caminho", que é a verdade e é o
   * que esta tela existe para mostrar.
   */
  // A conta é feita no RENDER, e não dentro do efeito: `setState` síncrono em
  // efeito é o que o lint proíbe, e com razão — a tela mostraria "aguardando"
  // por um render depois de o caminho já ter chegado. Mesmo conserto do
  // `useLive` (decisão 168f) e da `Miniatura`.
  const aguardandoOCaminho =
    cunhando !== null && (all.find((c) => c.id === cunhando)?.path.length ?? 0) === 0

  useEffect(() => {
    if (!aguardandoOCaminho) return
    const relogio = setTimeout(() => {
      setCunhando(null)
    }, ESPERA_MAXIMA)
    const passo = setInterval(reload, INTERVALO)
    return () => {
      clearTimeout(relogio)
      clearInterval(passo)
    }
  }, [aguardandoOCaminho, reload])
  const view = categoryTreeView(all)
  const parent = draft.parentId === ROOT ? null : (all.find((c) => c.id === draft.parentId) ?? null)

  // `kind` é herdado do pai e não pode divergir (Docs/02-Dominio/Category.md).
  // O seletor só aparece na raiz: com pai, não há escolha a oferecer, e
  // oferecê-la seria convidar a divergência que `CategoryKindMismatchError`
  // existe para recusar.
  const kind = parent === null ? draft.kind : parent.kind

  // **A colisão é pergunta da criação, e só dela.** No rename o slug não muda
  // (decisão 98), então o predicado responderia sobre um slug que não vai
  // existir — e a recusa era **silenciosa**: `submit()` saía cedo com o aviso na
  // tela, sem tocar na porta. Ver o docblock de `collisionHint`.
  const hint =
    renamingId === null
      ? collisionHint(draft.name, draft.parentId === ROOT ? null : draft.parentId, all)
      : null

  function change(patch: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...patch }))
    setFailure(null)
  }

  function reset() {
    setFolha(null)
    setDraft(EMPTY)
    setFailure(null)
  }

  async function submit() {
    setFailure(null)
    if (hint !== null || draft.name.trim() === '') return

    try {
      if (renamingId === null) {
        // **Sem `path` e sem `depth`**: as regras negam os dois ao cliente
        // inclusive no `create`, e quem os preenche é a Cloud Function.
        const input: NewCategory = {
          name: draft.name.trim(),
          parentId: draft.parentId === ROOT ? null : draft.parentId,
          kind,
          icon: draft.icon,
          color: draft.color,
        }
        const id = await categoryRepository.create(input)
        // O `path` chega DEPOIS, e por isso a tela espera por ele. Ver
        // `esperarOCaminho`.
        setCunhando(id)
      } else {
        // Só o nome. O slug **não** acompanha: ele mora no `path` gravado, e
        // mudá-lo quebraria todo caminho denormalizado já escrito (decisão 98).
        await categoryRepository.rename(renamingId, draft.name.trim())
      }
      reset()
      reload()
    } catch (error: unknown) {
      console.error('CategoriesScreen: a escrita falhou', error)
      setFailure(error instanceof Error ? error.message : 'Não foi possível salvar a categoria.')
    }
  }

  async function archive(category: Category) {
    setFailure(null)
    try {
      await categoryRepository.archive(category.id)
      reload()
    } catch (error: unknown) {
      console.error('CategoriesScreen: o arquivamento falhou', error)
      setFailure(error instanceof Error ? error.message : 'Não foi possível arquivar a categoria.')
    }
  }

  // **Categoria arquivada não aparece em seletor** — invariante 3 de
  // `Docs/02-Dominio/Category.md`. Ela continua na lista acima, porque continua
  // resolvendo o `categoryId` de dado antigo; o que ela não pode é receber
  // filha nova, que nasceria já pendurada em algo que saiu de circulação.
  //
  // A descendência de uma arquivada some junto: `flattenTree` desce a partir
  // das raízes, e cortar o galho corta o que está abaixo dele. É o certo —
  // criar sob a filha de uma arquivada tem o mesmo problema, um nível abaixo.
  const parentOptions = [
    { value: ROOT, label: 'Nenhum (raiz)' },
    ...flattenTree(vivas(view.roots)).map(({ node, level }) => ({
      value: node.category.id,
      label: `${'— '.repeat(level)}${node.category.name}`,
    })),
  ]

  function linha(node: CategoryNode, level: number) {
    const { category, defects } = node
    const arquivada = category.archivedAt !== null

    return (
      <li
        key={category.id}
        className="flex items-center gap-1 border-b border-[var(--border)]"
        // O recuo desenha a hierarquia sem uma segunda estrutura na tela: a
        // profundidade É a informação, e um "/" no meio do nome a esconderia.
        style={{ paddingLeft: `${String(level * 0.9)}rem` }}
      >
        {level > 0 && (
          <span aria-hidden="true" className="shrink-0 text-[var(--text-faint)]">
            ↳
          </span>
        )}

        {/* A LINHA INTEIRA renomeia. Antes eram dois links sublinhados com o
            nome da categoria dentro ("Renomear Mercado"), que é o que dava a
            eles um nome acessível único — o `aria-label` faz o mesmo sem
            gastar a linha com duas frases. */}
        <button
          type="button"
          aria-label={`Renomear ${category.name}`}
          onClick={() => {
            setFolha(category.id)
            setDraft({ ...EMPTY, name: category.name, parentId: category.parentId ?? ROOT })
            setFailure(null)
          }}
          className="alvo min-w-0 flex-1 flex-col items-start justify-center gap-0 py-1 text-left"
        >
          <span className={`block truncate text-sm ${arquivada ? 'text-[var(--text-muted)]' : ''}`}>
            {category.name}
          </span>
          <span className="block truncate text-[11px] text-[var(--text-faint)]">
            {[
              CATEGORY_KINDS[category.kind],
              category.isSystem ? 'do sistema' : null,
              arquivada ? 'arquivada' : null,
              category.path.length === 0
                ? category.id === cunhando
                  ? 'criando o caminho…'
                  : 'sem caminho'
                : null,
            ]
              .filter((x) => x !== null)
              .join(' · ')}
          </span>
        </button>

        {defects.length > 0 && (
          <span role="alert" className="shrink-0 text-[11px] text-[var(--negative)]">
            {defects.map((defect) => DEFECT_LABELS[defect]).join(' · ')}
          </span>
        )}

        {/* Arquivar é controle de LINHA, e por isso não é `Botao`: a regra da
            decisão 232 é que vidro é para ação de tela, e isto se repete uma
            vez por categoria. */}
        {!arquivada && (
          <button
            type="button"
            aria-label={`Arquivar ${category.name}`}
            onClick={() => {
              void archive(category)
            }}
            className="alvo shrink-0 px-2 text-xs text-[var(--text-muted)]"
          >
            Arquivar
          </button>
        )}
      </li>
    )
  }

  return (
    <section aria-label="Categorias">
      {/* **O criar sobe para o topo, ao lado do título** — o mesmo lugar do
          "Novo produto" no catálogo. Antes era um `<button>` sublinhado no fim
          de um formulário no fim da página: para criar uma categoria era
          preciso passar pela árvore inteira. */}
      <header className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h1 className="titulo text-2xl">Categorias</h1>
        <Botao
          type="button"
          onClick={() => {
            setFolha('nova')
            setDraft(EMPTY)
            setFailure(null)
          }}
        >
          <span aria-hidden="true">+</span>
          Nova categoria
        </Botao>
      </header>

      <p className="mb-4 text-sm text-[var(--text-muted)]">
        Onde o dinheiro da casa é agrupado. Uma categoria pode ficar dentro de outra — "Mercado"
        dentro de "Alimentação" —, e é isso que faz o Dashboard somar por cima e o detalhe abrir por
        baixo.
      </p>

      {state.status === 'loading' && (
        <p role="status" className="text-sm text-[var(--text-muted)]">
          Carregando as categorias…
        </p>
      )}
      {state.status === 'failed' && (
        <p role="alert" className="text-sm text-[var(--text-muted)]">
          Não foi possível carregar as categorias agora.
        </p>
      )}

      {state.status === 'loaded' && (
        <>
          <Glass level="frost" className="mb-4 p-4" as="div">
            <ul>
              {all.length === 0 && (
                <li className="py-2 text-sm text-[var(--text-muted)]">
                  Nenhuma categoria cadastrada.
                </li>
              )}
              {flattenTree(view.roots).map(({ node, level }) => linha(node, level))}
            </ul>
          </Glass>

          {view.detached.length > 0 && (
            <Glass
              level="frost"
              className="mb-4 p-4"
              as="section"
              aria-label="Categorias fora da árvore"
            >
              <h2 className="text-sm font-medium text-[var(--negative)]">
                Fora da árvore — nenhuma raiz chega até elas
              </h2>
              <p className="mb-2 text-xs text-[var(--text-muted)]">
                Órfãs de um pai apagado, ou presas num ciclo de parentId. Elas existem no banco e
                não aparecem em nenhuma soma por ancestral.
              </p>
              <ul>{flattenTree(view.detached).map(({ node, level }) => linha(node, level))}</ul>
            </Glass>
          )}
        </>
      )}

      <p className="text-xs text-[var(--text-muted)]">
        Categoria se arquiva, não se apaga: apagar libera o slug aos olhos da árvore e o histórico
        continua apontando para ele.
      </p>

      {folha !== null && (
        <FolhaDeBaixo
          titulo={renamingId === null ? 'Nova categoria' : 'Renomear categoria'}
          onFechar={reset}
        >
          <form
            aria-label={renamingId === null ? 'Nova categoria' : 'Renomear categoria'}
            onSubmit={(event) => {
              event.preventDefault()
              void submit()
            }}
          >
            {failure !== null && <Problem>{failure}</Problem>}

            <TextField
              id={`${ID}-nome`}
              label="Nome"
              value={draft.name}
              onChange={(name) => {
                change({ name })
              }}
              hint={hint}
              placeholder="Mercado"
            />

            {renamingId === null ? (
              <>
                <SelectField
                  id={`${ID}-pai`}
                  label="Categoria pai"
                  value={draft.parentId}
                  options={parentOptions}
                  onChange={(parentId) => {
                    change({ parentId })
                  }}
                />
                {parent === null ? (
                  <SelectField
                    id={`${ID}-tipo`}
                    label="Tipo"
                    value={draft.kind}
                    options={optionsOf(CATEGORY_KINDS).map((k) => ({
                      value: k,
                      label: CATEGORY_KINDS[k],
                    }))}
                    onChange={(value) => {
                      change({ kind: value as Category['kind'] })
                    }}
                  />
                ) : (
                  <p className="mb-2 text-xs text-[var(--text-muted)]">
                    Tipo herdado de {parent.name}: {CATEGORY_KINDS[kind]}
                  </p>
                )}
                <TextField
                  id={`${ID}-icone`}
                  label="Ícone"
                  value={draft.icon}
                  onChange={(icon) => {
                    change({ icon })
                  }}
                />
                <CorField
                  value={draft.color}
                  onChange={(color) => {
                    change({ color })
                  }}
                />
              </>
            ) : (
              <p className="mb-2 text-xs text-[var(--text-muted)]">
                Renomear muda só o nome. O slug fica onde está — ele mora no caminho gravado, e
                mudá-lo quebraria o histórico que aponta para ele.
              </p>
            )}

            <div className="mt-4 flex items-center gap-2">
              <Botao tom="primario" type="submit">
                {renamingId === null ? 'Criar categoria' : 'Salvar nome'}
              </Botao>
              <Botao type="button" onClick={reset}>
                Cancelar
              </Botao>
            </div>
          </form>
        </FolhaDeBaixo>
      )}
    </section>
  )
}
